import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { locationInfo } from '../mockData';
import { toast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'dry-cleaning',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!recaptchaToken) {
      toast({
        title: "Please verify",
        description: "Please complete the 'I'm not a robot' checkbox.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: "Message Sent!",
          description: "Thank you for contacting us. We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', service: 'dry-cleaning', message: '' });
        setRecaptchaToken(null);
        recaptchaRef.current?.reset();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to send. Please call us at 0207 622 4592.",
          variant: "destructive",
        });
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send. Please call us at 0207 622 4592.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden mt-24">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1556656793-08538906a9f8?q=80&w=2070&auto=format&fit=crop" alt="Contact us" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: '"Cinzel", serif' }}>Contact Us</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">Get in touch for expert garment care services</p>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-[#1F2D3D] mb-8" style={{ fontFamily: '"Cinzel", serif' }}>Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input type="text" name="name" required value={formData.name} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent" placeholder="John Smith" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent" placeholder="john@example.com" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent" placeholder="+44 20 7123 4567" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service Interested In *</label>
                  <select name="service" required value={formData.service} onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent">
                    <option value="dry-cleaning">Dry Cleaning</option>
                    <option value="commercial-laundry">Commercial Laundry</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <textarea name="message" required value={formData.message} onChange={handleChange} rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F2D3D] focus:border-transparent resize-none" placeholder="Tell us about your requirements..."></textarea>
                </div>

                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LdOFJwsAAAAAPc4tSaOMUjPQo9TD0d3N53rX0ul"
                    onChange={(token) => setRecaptchaToken(token)}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>

                <button type="submit" disabled={isSubmitting || !recaptchaToken}
                  className="w-full bg-[#1F2D3D] text-white py-4 px-6 rounded-lg font-medium hover:bg-[#1A2332] transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: '"Cinzel", serif' }}>
                  {isSubmitting ? 'Sending...' : <><Send className="mr-2" size={20} />Send Message</>}
                </button>
              </form>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-[#1F2D3D] mb-8" style={{ fontFamily: '"Cinzel", serif' }}>Get In Touch</h2>
              <div className="space-y-6 mb-8">
                <div className="flex items-start p-6 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D] rounded-full flex items-center justify-center mr-4">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-1" style={{ fontFamily: '"Cinzel", serif' }}>Phone</h3>
                    <a href={`tel:${locationInfo.phone}`} className="text-gray-600 hover:text-[#1F2D3D]">{locationInfo.phone}</a>
                    <p className="text-sm text-gray-500 mt-1">Available 7 days a week</p>
                  </div>
                </div>

                <div className="flex items-start p-6 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D] rounded-full flex items-center justify-center mr-4">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-1" style={{ fontFamily: '"Cinzel", serif' }}>Email</h3>
                    <a href={`mailto:${locationInfo.email}`} className="text-gray-600 hover:text-[#1F2D3D]">{locationInfo.email}</a>
                    <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start p-6 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#1F2D3D] rounded-full flex items-center justify-center mr-4">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1F2D3D] mb-1" style={{ fontFamily: '"Cinzel", serif' }}>Address</h3>
                    <p className="text-gray-600">{locationInfo.address}</p>
                    <p className="text-sm text-gray-500 mt-1">London's West End</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#1F2D3D] text-white p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: '"Cinzel", serif' }}>Opening Hours</h3>
                <p className="text-white/90">{locationInfo.hours.weekdays}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
